/**
 * Cores do Design System em hex, para as superfícies que não resolvem `var()`
 * (E54.F5.T02 / ADR-0065).
 *
 * O app inteiro lê cor por utilitário Tailwind — `bg-accent`, `text-fg-muted` —
 * que resolve `oklch(var(--nome))` em tempo de estilo. Duas superfícies da mesa
 * ficam fora desse caminho: o canvas Fabric do "Sobrepor & alinhar" (a cor vai
 * para o `CanvasRenderingContext2D` e para o parser de cor do Fabric, que não
 * conhecem `var()`) e os atributos de apresentação dos SVGs de marcação.
 *
 * Por isso o valor aparece aqui como hex — mas **não** é uma segunda paleta: o
 * guard `design_system_telas.test.ts` converte cada hex para OKLCH e o compara
 * com a var homônima do `index.css`. Se a paleta mudar e este arquivo ficar
 * para trás, o guard reprova.
 */
export const TOKEN_HEX = {
  /** `--paper`: a prancha sobre a qual o espécime é examinado. */
  paper: '#f5f5f8',
  /** `--bg`: o ground do sistema. */
  bg: '#f2f2f3',
  /** `--accent-700`: aço escuro dos controles de manipulação. */
  accent700: '#2c455d',
  /**
   * Paleta de INSTRUMENTO da gênese gráfica (E54.F6.T07). A ADR-0037 §C exige
   * que ela seja distinta da rampa de veredito — marcar uma extremidade não é
   * dizer nada sobre autoria —, e até aqui isso era cumprido com azul/violeta/
   * teal escritos à mão, fora do sistema. Passa a ser cumprido com três degraus
   * do próprio aço e do grafite: continuam sem parentesco com ok/warn/bad, e a
   * FORMA (círculo, losango, quadrado) segue como portadora primária do tipo.
   */
  /** `--accent`: aço base — extremidades. */
  accent: '#5980a6',
  /** `--accent-900`: aço escuro — junções. */
  accent900: '#1d2d3d',
  /** `--fg-dim`: grafite — paradas estimadas e caixa marcada à mão. */
  fgDim: '#5d5d60',
} as const;

/**
 * Rampa de sinal do pacote de design (E54.F7.T10), para as superfícies que
 * também não resolvem `var()` — aqui, as caixas do canvas Fabric da revisão de
 * extração.
 *
 * São os mesmos três hex das anotações da mesa (`ANNOTATION_COLOR_HEX`), e é
 * de propósito que apareçam duas vezes: lá o nome da chave é a semântica
 * persistida da anotação ("o perito escolheu verde"), aqui é o estado da
 * decisão ("a região foi aceita"). O guard confere ambos contra o pacote
 * versionado, então a duplicação não pode divergir em silêncio.
 */
export const SIGNAL_HEX = {
  /** `--sc-pos-500`: região aceita pelo perito. */
  pos: '#5f9873',
  /** `--sc-warn-500`: região sugerida, ainda sem decisão. */
  warn: '#c79445',
  /** `--sc-neg-500`: região descartada. */
  neg: '#c4635f',
} as const;

export type SignalHexName = keyof typeof SIGNAL_HEX;

export type TokenHexName = keyof typeof TOKEN_HEX;
